import { CardDetails } from "@capitalos/react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BanknoteArrowUp,
  HomeIcon,
  PackageCheckIcon,
  RotateCcw,
  TruckIcon,
  UserIcon,
  WalletCards,
} from "lucide-react";
import { capitalize, formatCurrency, satisfiesRole } from "shared";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import {
  ApprovalPolicyDetails,
  ExpensePolicyDetails,
} from "@/components/policy/policy-details";
import { CreditCardSkeleton } from "@/components/skeletons/card-skeleton";
import { SpendProgressBar } from "@/components/spend-progress-bar";
import { RenderAllowedCategories } from "@/components/table/cells/RenderAllowedCategories";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heading, Text } from "@/components/ui/text";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { CardFrozenMessage } from "./-components/card-frozen-message";
export const Route = createFileRoute("/_authed/_org/cards/$id/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { trpc } = Route.useRouteContext();
  const { admin } = useRequiredRole();

  const { mutate: resetSpend } = useMutation(
    trpc.card.resetSpend.mutationOptions({
      onSuccess: () => {
        // handleInvalidations();
        toast.success("Card spend was reset", {
          position: "bottom-left",
        });
      },
    }),
  );

  const { data: card } = useSuspenseQuery(
    trpc.card.get.queryOptions({
      cardId: id,
    }),
  );

  const { data: session } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const isCardHolder =
    card.cardHolderId === session?.user?.id ||
    satisfiesRole(session?.role, "admin");
  const { restriction } = card;

  return (
    <div className="mb-4 flex flex-col space-y-4 overflow-y-auto px-6">
      {isCardHolder && card.status !== "canceled" && (
        <div>
          <div className="flex flex-row items-center justify-between">
            <Heading size="internal" variant="internal">
              Your {capitalize(card.type)} Card
            </Heading>
            <a
              className="flex flex-row items-center text-xs hover:underline"
              href="https://docs.givefront.com/cards#add-cards-to-apple-and-google-wallet"
              target="_blank"
              rel="noopener"
            >
              <WalletCards className="mr-1 size-4" />
              <span>Add to Apple or Google wallet</span>
            </a>
          </div>

          <div className="mt-4 flex w-full flex-row items-center justify-center">
            <div className="h-60 w-96">
              <CardDetails
                cardId={card.capitalOsCardId}
                cardOnly
                loadingComponent={<CreditCardSkeleton />}
              />
            </div>
          </div>

          {card.type === "physical" && admin && (
            <div className="mt-5 rounded-md border border-blue-200 bg-blue-50 p-3">
              <Text className="text-blue-800 text-sm">
                <strong>Security tip:</strong> don't use physical card details
                with merchants who keep your card on file. Use a
                <strong> virtual card </strong>
                which can be cancelled anytime.
              </Text>
            </div>
          )}

          {restriction.lockAt && (
            <div className="mt-4 text-center">
              <Text className="text-muted-foreground text-sm">
                Card will be locked on{" "}
                <span className="font-medium">
                  {new Date(restriction.lockAt).toLocaleDateString("en-US")}
                </span>
              </Text>
            </div>
          )}
        </div>
      )}
      <CardFrozenMessage card={card} />

      <SpendProgressBar
        spendLimit={{
          ...restriction,
          balanceCents: restriction.periodBalanceCents,
          targetCents: restriction.periodTargetCents,
        }}
        className={twMerge(
          restriction.refreshInterval === "manual" && "mb-2!",
          "mt-2 grow",
        )}
      />

      {restriction.refreshInterval === "manual" && admin && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              className="self-end"
              size="sm"
              disabled={restriction.periodBalanceCents === 0}
            >
              <RotateCcw />
              Reset Spend
            </Button>
          </AlertDialogTrigger>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Are you sure you want to reset spend for {card.type} card "
                {card.name}" ending in {card.last4}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will reset the card's balance from{" "}
                {formatCurrency(restriction.periodBalanceCents)} to $0.00 and
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => resetSpend({ cardId: card.id })}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {!admin && isCardHolder && (
        <Link
          to="/requests/create/limit-increase"
          search={{ cardId: card.id }}
          className="mt-1 w-full"
        >
          <Button variant="outline" size="sm" className="w-full">
            <BanknoteArrowUp className="size-4 text-neutral-500" />
            Request higher limit
          </Button>
        </Link>
      )}

      <div className="mt-8 flex w-full flex-col gap-2">
        <Heading size="internal" variant="internal">
          Expense Policy
        </Heading>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50/50 p-4">
          {card.policy ? (
            <ExpensePolicyDetails policy={card.policy} />
          ) : (
            <span className="font-medium text-neutral-400 text-sm">None</span>
          )}
        </div>

        {card.approvalPolicy && (
          <>
            <Heading size="internal" variant="internal" className="mt-4">
              Approval Policy
            </Heading>

            <ApprovalPolicyDetails {...card.approvalPolicy} />
          </>
        )}
      </div>
      <div className="flex w-full flex-col gap-2">
        <Heading size="internal" variant="internal">
          Allowed Categories
        </Heading>

        <RenderAllowedCategories
          allowedCategories={restriction.allowedCategories}
          maxDisplay={6}
        />
      </div>

      {card.physicalCardInfo?.shipment && (
        <>
          <Heading>Shipping</Heading>

          <Card className="w-full">
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="mb-1 flex items-center">
                      <UserIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Recipient</span>
                    </div>
                    <p className="text-sm">
                      {card.physicalCardInfo.shipment.recipientName ||
                        "Not specified"}
                    </p>
                  </div>

                  <div>
                    <div className="mb-1 flex items-center">
                      <TruckIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">
                        Shipping Method
                      </span>
                    </div>
                    <p className="text-sm">
                      {card.physicalCardInfo.shipment.method
                        ? card.physicalCardInfo.shipment.method.toUpperCase()
                        : "Standard"}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="mb-1 flex items-center">
                    <HomeIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">
                      Shipping Address
                    </span>
                  </div>
                  {card.physicalCardInfo.shipment.address ? (
                    <div className="space-y-1 text-sm">
                      <p>
                        {card.physicalCardInfo.shipment.address.addressLine1}
                      </p>
                      {card.physicalCardInfo.shipment.address.addressLine2 && (
                        <p>
                          {card.physicalCardInfo.shipment.address.addressLine2}
                        </p>
                      )}
                      <p>
                        {card.physicalCardInfo.shipment.address.city},{" "}
                        {card.physicalCardInfo.shipment.address.state}{" "}
                        {card.physicalCardInfo.shipment.address.zipcode}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm">Address not specified</p>
                  )}
                </div>

                <div>
                  <div className="mb-1 flex items-center">
                    <PackageCheckIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Shipping Status</span>
                  </div>
                  <div className="flex items-center">
                    <RenderStatusBadge
                      className="text-xs"
                      status={
                        card.physicalCardInfo.shipment.status || "pending"
                      }
                    >
                      {capitalize(
                        card.physicalCardInfo.shipment.status || "pending",
                      )}
                    </RenderStatusBadge>

                    {card.physicalCardInfo.shipment.shippedAt && (
                      <span className="ml-2 text-muted-foreground text-xs">
                        Shipped on{" "}
                        {new Date(
                          card.physicalCardInfo.shipment.shippedAt,
                        ).toLocaleDateString("en-US")}
                      </span>
                    )}
                  </div>

                  {card.physicalCardInfo.shipment.trackingNumber && (
                    <div className="mt-2 text-sm">
                      <span className="font-medium">Tracking:</span>
                      {card.physicalCardInfo.shipment.trackingUrl ? (
                        <a
                          className="ml-1 text-primary hover:underline"
                          href={card.physicalCardInfo.shipment.trackingUrl}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {card.physicalCardInfo.shipment.trackingNumber}
                        </a>
                      ) : (
                        <span className="ml-1">
                          {card.physicalCardInfo.shipment.trackingNumber}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
